import { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactClassificationDtoIn } from '@/models/in/SupportedDtoIn';
import { getExpirationStatus, getExpirationStatusColor } from '@/helpers/contactClassificationExpiration';
import { ContactClassificationStatus } from '@/models/ContactClassificationStatus';
import { DocumentStatus } from '@/models/common/DocumentStatus';
import Button from '@/components/ui/Button';
import { getClassificationStatusLabel } from '@/helpers/classificationStatusLabel';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import styles from './ClassificationCard.module.css';

interface ClassificationCardProps {
  classification: ContactClassificationDtoIn;
  onStatusChange: (newStatus: ContactClassificationStatus) => void;
  onOpenOfficeComments?: () => void;
  onRequestCompletion?: () => void;
}


// Get available status options based on current status
const getAvailableStatusOptions = (currentStatus: ContactClassificationStatus): ContactClassificationStatus[] => {
  const allStatuses = [
    ContactClassificationStatus.PendingApproval,
    ContactClassificationStatus.PendingCompletion,
    ContactClassificationStatus.Approved,
    ContactClassificationStatus.Rejected,
    ContactClassificationStatus.Cancelled,
    ContactClassificationStatus.CompletionReturned,
  ];
  return allStatuses.filter(s => s !== currentStatus);
};

const ClassificationCard = ({
  classification,
  onStatusChange,
  onOpenOfficeComments,
  onRequestCompletion,
}: ClassificationCardProps) => {
  const { t } = useTranslation();

  // Count total comments from both sources
  // Skip index 0 - synthetic "הגשת בקשה" entry not stored in DB
  const totalCommentsCount = useMemo(() => {
    const statusHistoryComments = (classification.statusHistory || [])
      .slice(1)
      .filter(entry => entry.officeComment && entry.officeComment.comment).length;
    const standaloneComments = (classification.officeComments || []).length;
    return statusHistoryComments + standaloneComments;
  }, [classification.statusHistory, classification.officeComments]);

  const expirationStatus = getExpirationStatus(classification);
  const expirationColor = getExpirationStatusColor(expirationStatus);

  const availableStatuses = getAvailableStatusOptions(classification.status);

  // Sort status history by date, newest first
  const sortedStatusHistory = useMemo(() => {
    if (!classification.statusHistory || classification.statusHistory.length === 0) {
      return [];
    }
    return [...classification.statusHistory].sort(
      (a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
    );
  }, [classification.statusHistory]);

  const handleStatusSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value as ContactClassificationStatus;
    if (newStatus) {
      if (newStatus === ContactClassificationStatus.PendingCompletion && onRequestCompletion) {
        onRequestCompletion();
      } else {
        onStatusChange(newStatus);
      }
      e.target.value = '';
    }
  };

  const expiryText = classification.expiresTimestamp
    ? `${t('validUntil')} ${new Date(classification.expiresTimestamp).toLocaleDateString('he-IL')}`
    : '';

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <span
            className={styles.statusIndicator}
            style={{ '--status-color': expirationColor } as CSSProperties}
          />
          <span className="body-md-bold text-(--font-sub-title)">
            {t(classification.classification.name)}
          </span>
        </div>
        {expiryText && (
          <span className="body-sm-regular text-(--font-body-2)">
            {expiryText}
          </span>
        )}
      </div>

      {/* Documents Section */}
      <div className={styles.documentsSection}>
        <span className="body-sm-semibold text-(--font-body-1)">{t('documents')}</span>
        {classification.documents && classification.documents.length > 0 ? (
          <table className={styles.documentsTable}>
            <thead>
              <tr>
                <th>{t('documentName')}</th>
                <th>{t('status')}</th>
                <th>{t('officeComment')}</th>
                <th>{t('userComment')}</th>
              </tr>
            </thead>
            <tbody>
              {classification.documents.map((doc, idx) => (
                <tr key={idx}>
                  <td>
                    <div className="flex items-center gap-1">
                      <a href={doc.uri} target="_blank" rel="noreferrer" className={styles.documentLink}>
                        {doc.name}
                      </a>
                      <DownloadFileButton uri={doc.uri} fileName={doc.name} />
                    </div>
                  </td>
                  <td>
                    {doc.status === DocumentStatus.Rejected
                      ? t('documentStatusRejected')
                      : doc.status
                        ? doc.status
                        : '-'}
                  </td>
                  <td>{doc.officeComment || '-'}</td>
                  <td>{doc.userComment || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <span className="body-sm-regular text-(--font-body-2)">{t('noDocumentsAttached')}</span>
        )}
      </div>

      {/* Comments Section */}
      <div className={styles.commentsSection}>
        <span className="body-sm-semibold text-(--font-body-1)">{t('comments')}</span>
        {classification.comments ? (
          <p className="body-sm-regular text-(--font-body-1)">{classification.comments}</p>
        ) : (
          <span className="body-sm-regular text-(--font-body-2)">{t('noCommentsWritten')}</span>
        )}
      </div>

      {/* Status History Section */}
      <div className={styles.historySection}>
        <span className="body-sm-semibold text-(--font-body-1)">{t('statusHistory')}</span>
        {sortedStatusHistory.length > 0 ? (
          <table className={styles.historyTable}>
            <thead>
              <tr>
                <th>{t('date')}</th>
                <th>{t('fromStatus')}</th>
                <th>{t('toStatus')}</th>
                <th>{t('note')}</th>
                <th>{t('changedBy')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedStatusHistory.map((entry, idx) => {
                const isSubmission = entry.oldStatus === entry.newStatus;
                return (
                  <tr key={idx}>
                    <td>{new Date(entry.changedAt).toLocaleDateString('he-IL')}</td>
                    <td>{isSubmission ? '-' : getClassificationStatusLabel(entry.oldStatus, t)}</td>
                    <td>{getClassificationStatusLabel(entry.newStatus, t)}</td>
                    <td>{entry.officeComment?.comment || '-'}</td>
                    <td>{entry.changedByUserEmail || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <span className="body-sm-regular text-(--font-body-2)">{t('noStatusHistory')}</span>
        )}
      </div>

      {/* Office Comments Button */}
      {onOpenOfficeComments && (
        <div className={styles.officeCommentsSection}>
          <span className="body-sm-semibold text-(--font-body-1)">{t('officeComments.title')}:</span>
          <button className={styles.officeCommentsButton} onClick={onOpenOfficeComments}>
            {t('officeComments.manageComments')}
            {totalCommentsCount > 0 && (
              <span className={styles.officeCommentsBadge}>{totalCommentsCount}</span>
            )}
          </button>
        </div>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        {onRequestCompletion && classification.documents && classification.documents.length > 0 && (
          <Button variant="outline" onClick={onRequestCompletion}>
            {t('documents.requestCompletion')}
          </Button>
        )}
        <div className={styles.statusChangeSection}>
          <label className="body-sm-semibold text-(--font-body-1)">
            {t('changeStatusTo')}:
          </label>
          <select
            className={styles.statusDropdown}
            onChange={handleStatusSelect}
            defaultValue=""
          >
            <option value="" disabled>
              {getClassificationStatusLabel(classification.status, t)}
            </option>
            {availableStatuses.map((status) => (
              <option key={status} value={status}>
                {getClassificationStatusLabel(status, t)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default ClassificationCard;
